import { FC, useEffect, useMemo, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { deserializeStemFromStorage } from '../../../stores/song/stem/StemUtils'
import { ArpMediaType } from '../../../types/common'
import { getStemOpenseaLink } from '../../../utils/media'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Column, Row } from '../../core/Components'
import { formatEpochTimestamp } from '../../core/helpers/dates'
import { prettyPrintWalletAddress } from '../../core/Utils'
import Contributors from '../../platform/explore/common/Contributors'
import { InfoRow } from '../../profile/media-content/ProfileCurrentlyPlaying'
import {
  MediaExtraInfo,
  MediaInfoButtons,
  MediaInfoColumn,
  MediaInfoTitle
} from '../shared/MediaInfo'

export const useStemInfo = ({ tokenId }: { tokenId: number }) => {
  const [openseaLink, setOpenseaLink] = useState<string>(null)
  const [data, setData] = useState<IArpMedia>(null)

  useEffect(() => {
    ArpRegistry.getByIdAsync(tokenId).then((stem) => setData(stem))
    getStemOpenseaLink(tokenId).then((link) => setOpenseaLink(link))
  }, [tokenId])

  const arp = useMemo(() => {
    if (!data) return null
    return deserializeStemFromStorage(data.metadata.arpeggiComposition)
  }, [data])
  const loopCount = arp && arp.loops.length

  const createTime = data?.createdAt && parseInt(data.createdAt)
  const mintDate = createTime && formatEpochTimestamp(createTime, 'MM/DD/YY')

  return { stem: data, isReady: data !== null, loopCount, mintDate, openseaLink }
}

interface Props {
  index: number
}
const StemInfo: FC<Props> = ({ index }) => {
  const { stem, isReady, loopCount, mintDate, openseaLink } = useStemInfo({
    tokenId: index
  })

  if (!isReady) {
    return <CenteredLoadingAnimation />
  }

  return (
    <Column css={{ width: '100%' }}>
      <MediaInfoTitle
        title={stem.metadata.title}
        artistEthAddress={stem.user.walletAddress}
        artistName={stem.user.displayName}
      />
      {stem.remixCount > 0 && (
        <MediaExtraInfo css={{ marginTop: '35px' }}>
          Times Remixed: {stem.remixCount}
        </MediaExtraInfo>
      )}
      <Row
        css={{
          fontSize: '14px',
          marginTop: !stem.remixCount && '50px',
          flexDirection: 'column',
          '@tablet': { flexDirection: 'row' }
        }}
      >
        <MediaInfoColumn title="Overview" css={{ flex: '1', '@tablet': { marginRight: '25px' } }}>
          <InfoRow title="Contract Address" tight>
            {prettyPrintWalletAddress(stem.contractAddress || '')}
          </InfoRow>
          <InfoRow title="Token ID" tight>
            {stem.tokenId}
          </InfoRow>
          <InfoRow title="Blockchain" tight>
            Polygon
          </InfoRow>
        </MediaInfoColumn>
        <MediaInfoColumn title="Stem properties" css={{ flex: '1' }}>
          <InfoRow title="Genre" tight>
            {stem.metadata.genres[0]}
          </InfoRow>
          <InfoRow title="# of Loops" tight>
            {loopCount}
          </InfoRow>
          <InfoRow title="Mint date" tight>
            {mintDate}
          </InfoRow>
        </MediaInfoColumn>
      </Row>
      <Row>
        <MediaInfoColumn title="Contributors">
          <Contributors
            index={index}
            type={ArpMediaType.STEM}
            noText
            avatarSize="38px"
            maxContributors={10}
            css={{ marginTop: '0px' }}
          />
        </MediaInfoColumn>
      </Row>
      <MediaInfoButtons type={ArpMediaType.STEM} index={index} openseaLink={openseaLink} />
    </Column>
  )
}

export default StemInfo
